import { ComponentPropsWithoutRef, forwardRef, ReactNode, useState } from 'react';

import {
  ArrowDown,
  ArrowUp,
  Building2,
  CalendarDays,
  CalendarOff,
  CornerDownLeft,
  FolderKanban,
  LucideIcon,
  Plus,
  Receipt,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import {
  ReportDimensionEntityDTO,
  ReportDimensionResponse,
  ReportDimensionSourceDTO,
} from '../types/report-dimension';
import { CustomDimensionTag } from './custom-dimension-tag';

/** Section order in the picker — the entity a report is "about" first, time last. */
const ENTITY_ORDER: ReportDimensionEntityDTO[] = [
  ReportDimensionEntityDTO.MEMBER,
  ReportDimensionEntityDTO.PROJECT,
  ReportDimensionEntityDTO.CLIENT,
  ReportDimensionEntityDTO.TIME_OFF,
  ReportDimensionEntityDTO.OVERHEAD,
  ReportDimensionEntityDTO.TIME,
];

const ENTITY_ICONS: Record<ReportDimensionEntityDTO, LucideIcon> = {
  [ReportDimensionEntityDTO.MEMBER]: Users,
  [ReportDimensionEntityDTO.PROJECT]: FolderKanban,
  [ReportDimensionEntityDTO.CLIENT]: Building2,
  [ReportDimensionEntityDTO.TIME_OFF]: CalendarOff,
  [ReportDimensionEntityDTO.OVERHEAD]: Receipt,
  [ReportDimensionEntityDTO.TIME]: CalendarDays,
};

interface HighlightedLabelProps {
  label: string;
  query: string;
}

const HighlightedLabel = ({ label, query }: HighlightedLabelProps) => {
  const index = query ? label.toLowerCase().indexOf(query) : -1;
  if (index < 0) return <>{label}</>;
  return (
    <>
      {label.slice(0, index)}
      <mark className="bg-transparent font-semibold text-inherit">
        {label.slice(index, index + query.length)}
      </mark>
      {label.slice(index + query.length)}
    </>
  );
};

interface DimensionPickerProps {
  dimensions: ReportDimensionResponse[];
  /** Keys already in use by the element — shown as "Added" and not selectable. */
  usedKeys: Array<string | null>;
  onSelect: (key: string) => void;
  /** Open limited to one entity's fields; the user can clear it to see every field. */
  scope?: ReportDimensionEntityDTO;
  align?: 'start' | 'end';
  /** The trigger element; must forward its ref (e.g. `DimensionPickerTrigger` or a `button`). */
  children: ReactNode;
}

/**
 * Searchable field picker for report dimensions, grouped into sections by the entity each field
 * describes. Replaces the "+ Label" pill lists for rows, pivot, X axis, series and slices.
 */
export const DimensionPicker = ({
  dimensions,
  usedKeys,
  onSelect,
  scope,
  align = 'start',
  children,
}: DimensionPickerProps) => {
  const { t } = useTranslation('authenticated.reports');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeScope, setActiveScope] = useState<ReportDimensionEntityDTO | undefined>(scope);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setSearch('');
      setActiveScope(scope);
    }
  };

  const select = (key: string) => {
    onSelect(key);
    setOpen(false);
  };

  const used = new Set(usedKeys);
  const query = search.trim().toLowerCase();
  const entityLabel = (entity: ReportDimensionEntityDTO) => t(`dimensionPicker.entity.${entity}`);

  const visible = dimensions.filter(
    d =>
      (!activeScope || d.entity === activeScope) &&
      (!query ||
        d.label.toLowerCase().includes(query) ||
        entityLabel(d.entity).toLowerCase().includes(query))
  );
  const sections = ENTITY_ORDER.map(entity => ({
    entity,
    items: visible.filter(d => d.entity === entity),
  })).filter(section => section.items.length > 0);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-0">
        {/* Filtering is done here (not by cmdk) so a match can also come from the section name
            and the matched text can be highlighted. */}
        <Command shouldFilter={false} loop>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={t('dimensionPicker.searchPlaceholder')}
          />
          {activeScope && (
            <div className="px-3 pt-2">
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted py-px pl-2.5 pr-1 text-xs">
                {t('dimensionPicker.scopedTo', { entity: entityLabel(activeScope) })}
                <button
                  type="button"
                  onClick={() => setActiveScope(undefined)}
                  aria-label={t('dimensionPicker.showAllFields')}
                  className="grid h-4 w-4 place-items-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}
          <CommandList className="max-h-80">
            <CommandEmpty className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('dimensionPicker.noMatches', { query: search.trim() })}
            </CommandEmpty>
            {sections.map(({ entity, items }) => {
              const Icon = ENTITY_ICONS[entity];
              return (
                <CommandGroup
                  key={entity}
                  heading={
                    <span className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5" />
                      {entityLabel(entity)}
                    </span>
                  }
                >
                  <div className="ml-3.5 border-l pl-1.5">
                    {items.map(d => {
                      const isUsed = used.has(d.key);
                      const isCustom = d.source === ReportDimensionSourceDTO.CUSTOM_ATTRIBUTE;
                      return (
                        <CommandItem
                          key={d.key}
                          value={d.key}
                          disabled={isUsed}
                          onSelect={() => select(d.key)}
                          className="data-[disabled=true]:opacity-60"
                        >
                          <span className="min-w-0 flex-1 truncate">
                            <HighlightedLabel label={d.label} query={query} />
                          </span>
                          {isUsed ? (
                            <span className="shrink-0 text-xs italic text-muted-foreground">
                              {t('dimensionPicker.added')}
                            </span>
                          ) : (
                            isCustom && (
                              <>
                                {d.customAttributeType && (
                                  <span className="shrink-0 text-xs text-muted-foreground">
                                    {t(
                                      `dimensionPicker.customAttributeType.${d.customAttributeType}`
                                    )}
                                  </span>
                                )}
                                <CustomDimensionTag />
                              </>
                            )
                          )}
                        </CommandItem>
                      );
                    })}
                  </div>
                </CommandGroup>
              );
            })}
          </CommandList>
          <div className="flex items-center gap-3 border-t px-3 py-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <ArrowUp className="h-3 w-3" />
              <ArrowDown className="h-3 w-3" />
              {t('dimensionPicker.hintMove')}
            </span>
            <span className="flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" />
              {t('dimensionPicker.hintAdd')}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="font-sans">{t('dimensionPicker.keyEsc')}</kbd>
              {t('dimensionPicker.hintClose')}
            </span>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

type DimensionPickerTriggerProps = ComponentPropsWithoutRef<'button'>;

/** Dashed "+ Add …" button that opens a `DimensionPicker`. */
export const DimensionPickerTrigger = forwardRef<HTMLButtonElement, DimensionPickerTriggerProps>(
  ({ className, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex items-center gap-1.5 self-start rounded-md border border-dashed px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-solid hover:border-foreground hover:text-foreground data-[state=open]:border-solid data-[state=open]:border-foreground data-[state=open]:text-foreground',
        className
      )}
      {...props}
    >
      <Plus className="h-3.5 w-3.5" />
      {children}
    </button>
  )
);
DimensionPickerTrigger.displayName = 'DimensionPickerTrigger';
